import Decimal from "decimal.js-light";
import formatMoney from "src/utils/formatMoney";
import { AmountType, discounts, DiscountType } from "types/code-generator";

interface Props {
  discount: discounts["discounts"][0];
  omitWarning?: boolean;
}

export function DiscountSummary(props: Props) {
  const content = [];

  const min = props.discount.min || 0;
  const max = props.discount.max || 0;

  let minMaxValues = [];

  for (let i = min; i <= max; i++) {
    if (i === max) {
      minMaxValues.push(`or ${i}`);
    } else {
      minMaxValues.push(i);
    }
  }

  let minMaxDisplay = minMaxValues.join(", ");

  if (min === max) {
    minMaxDisplay = min.toString();
  }

  if (
    props.discount.discountType === DiscountType.MultiClass &&
    props.discount.amountType === AmountType.Percent
  ) {
    const discountDisplay = `${new Decimal(props.discount.amount || 0)
      .times(100)
      .toNumber()}%`;

    content.push(
      <div key="mc-p" className="mb-2">
        When a single participant is enrolled in{" "}
        <strong>{minMaxDisplay} offerings</strong> that have this discount
        enabled, the account will receive a <strong>{discountDisplay}</strong>{" "}
        discount off each eligible offering.
      </div>
    );
  }

  if (
    props.discount.discountType === DiscountType.MultiStudent &&
    props.discount.amountType === AmountType.Percent
  ) {
    const discountDisplay = `${new Decimal(props.discount.amount || 0)
      .times(100)
      .toNumber()}%`;

    content.push(
      <div key="mc-p" className="mb-2">
        When an account has <strong>{minMaxDisplay} participants</strong>{" "}
        enrolled in offerings that have this discount enabled, the account will
        receive a <strong>{discountDisplay}</strong> discount off each eligible
        offering.
      </div>
    );
  }

  if (
    props.discount.discountType === DiscountType.MultiClass &&
    props.discount.amountType === AmountType.Dollar
  ) {
    for (let i = min; i <= max; i++) {
      const discountDisplay = `$${formatMoney(
        props.discount.amount ? props.discount.amount * i : 0
      )}`;

      content.push(
        <div key={i} className="mb-2">
          When a single participant is enrolled in{" "}
          <strong>{i} offerings</strong> that have this discount enabled, the
          account will receive a <strong>{discountDisplay}</strong> total
          discount, regardless of any price proration.
        </div>
      );
    }
  }

  if (
    props.discount.discountType === DiscountType.MultiStudent &&
    props.discount.amountType === AmountType.Dollar
  ) {
    for (let i = min; i <= max; i++) {
      const discountDisplay = `$${formatMoney(
        props.discount.amount ? props.discount.amount * i : 0
      )}`;

      content.push(
        <div key={i} className="mb-2">
          When an account has <strong>{i} participants</strong> enrolled in
          offerings that have this discount enabled, the account will receive a{" "}
          <strong>{discountDisplay}</strong> total discount, regardless of any
          price proration.
        </div>
      );
    }
  }

  if (!props.omitWarning) {
    content.push(
      <div className="flex pt-2" key="mc-d">
        <div className="mr-2">
          <i className="far fa-triangle-exclamation text-main" />
        </div>
        <div className="flex-1 text-sm">
          At the time of checkout, a customer’s cart must include all eligible
          registrations to receive a discount. There is no retroactive discount
          for prior registrations; however, the discount will apply to all
          future recurring invoices.
        </div>
      </div>
    );
  }

  return <>{content}</>;
}
